import { Container, Singleton } from 'typescript-ioc/es5'
import { AppService } from './app'
import aqe from '@pushly/aqe'
import { HttpMethod } from '@pushly/aqe/lib/enums'
import { IApiCallOptions, IApiDeleteResponse, IApiResponse } from '@pushly/aqe/lib/interfaces'
import { EventWebhook } from '@pushly/models/lib/structs/event-webhooks/event-webhook'
import { CreateEventWebhookRequestDto } from '@pushly/models/lib/structs/event-webhooks/create-event-webhook-request-dto'
import { UpdateEventWebhookRequestDto } from '@pushly/models/lib/structs/event-webhooks/update-event-webhook-request-dto'
import { AbstractRequestService } from '@pushly/aqe/lib/services/abstract-request-service'
import { ApiVersion } from '../enums/api-version.enum'

@Singleton
export class DomainWebhookService extends AbstractRequestService {
    public appService: AppService

    public constructor() {
        super()
        this.appService = Container.get(AppService)
    }

    public async list(
        domainId: number,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<EventWebhook[]>> {
        if (options.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            return this.request(HttpMethod.GET, this.getEndpoint(domainId, options.version), {
                ...options,
                config: { skipCaseConversion: true },
                responseType: [EventWebhook],
            })
        } finally {
            if (options.showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }
        }
    }

    public async create(
        domainId: number,
        dto: CreateEventWebhookRequestDto,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<EventWebhook>> {
        if (options.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            return this.request(HttpMethod.POST, this.getEndpoint(domainId, options.version), {
                ...options,
                data: dto,
                config: { skipCaseConversion: true },
                responseType: EventWebhook,
            })
        } finally {
            if (options.showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }
        }
    }

    public async update(
        domainId: number,
        webhookId: string,
        dto: UpdateEventWebhookRequestDto,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<EventWebhook>> {
        if (options.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            return this.request(HttpMethod.PATCH, `${this.getEndpoint(domainId, options.version)}/${webhookId}`, {
                ...options,
                data: dto,
                config: { skipCaseConversion: true },
                responseType: EventWebhook,
            })
        } finally {
            if (options.showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }
        }
    }

    public async archive(
        domainId: number,
        webhookId: string,
        options: IApiCallOptions = {},
    ): Promise<IApiDeleteResponse> {
        if (options.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            return this.requestDelete(`${this.getEndpoint(domainId, options.version)}/${webhookId}`, {
                ...options,
                config: { skipCaseConversion: true },
            })
        } finally {
            if (options.showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }
        }
    }

    public async getSigningSecretMetadata(
        domainId: number,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<any>> {
        if (options.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            return this.request(HttpMethod.GET, `${this.getEndpoint(domainId, options.version)}/signing-secret`, {
                ...options,
                config: { skipCaseConversion: true },
            })
        } finally {
            if (options.showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }
        }
    }

    public async revealSigningSecret(
        domainId: number,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<any>> {
        if (options.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            return this.request(HttpMethod.POST, `${this.getEndpoint(domainId, options.version)}/signing-secret/reveal`, {
                ...options,
                config: { skipCaseConversion: true },
                responseType: Object as any,
            })
        } finally {
            if (options.showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }
        }
    }

    public async regenerateSigningSecret(
        domainId: number,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<any>> {
        if (options.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            return this.request(HttpMethod.POST, `${this.getEndpoint(domainId, options.version)}/signing-secret/regenerate`, {
                ...options,
                config: { skipCaseConversion: true },
                responseType: Object as any,
            })
        } finally {
            if (options.showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }
        }
    }

    private getEndpoint = (domainId: number, version: ApiVersion | undefined) =>
        `${aqe.defaults.publicApiDomain}/${version ?? ApiVersion.V3}domains/${domainId}/event-webhooks`
}
